/**
 * Visual Builder — Super Admin.
 *
 * Elementor-style 3-column visual editor for BAMKO Global Retail.
 *
 * Phase 1 (delivered):
 *   • Target selector (Customer App / Website / Store Admin / POS)
 *   • Platform-aware component palette
 *   • All Stores / Selected Stores scoping (parallel PATCH isolation)
 *   • Iframe live preview for Website · Mock preview for other targets
 *   • GET/PATCH /ui-customization?platform=X&store_id=Y
 *
 * Phase 2 (this file):
 *   • Real drag-and-drop from LEFT palette → CENTER canvas (native HTML5 DnD)
 *   • Reorder placed components within the canvas by drag-and-drop
 *   • Global Style pane — colours, typography, radii, spacing tokens
 *   • Richer per-component settings (border, margin, alignment, width, image, icon)
 *   • Image upload reuses existing Cloudinary infra (uploadToCloudinary)
 *   • Icon picker reuses lucide-react (already installed, no new deps)
 *   • Undo/Redo/Reset cover EVERY edit (sections, components, global_style)
 *   • All edits stay LOCAL until "Save & Apply" (no per-keystroke PATCH)
 *
 * Storage schema written to /ui-customization?platform=X:
 *   {
 *     sections:     [ { id, comp } ],           // ordered list of placed components
 *     components:   { [compId]: { ...safe UI settings } },
 *     global_style: { primary, secondary, ..., card_radius, ... },
 *     // legacy website fields (homepage/card/header/pdp) untouched
 *   }
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import * as LucideIcons from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { uploadToCloudinary, cldOptimize } from "@/lib/cloudinary";
import {
  Undo2, Redo2, RotateCcw, Monitor, Tablet, Smartphone, Save,
  Sparkles, Layers, Settings2, ChevronRight, Search as SearchIcon,
  Target as TargetIcon, Store, Check, X, Info, Loader2, Palette,
  UploadCloud, Image as ImageIcon, Trash2, GripVertical, ArrowUp, ArrowDown,
  AlignLeft, AlignCenter, AlignRight, Plus,
} from "lucide-react";
import {
  PLATFORMS, groupsFor, componentById,
  GLOBAL_STYLE_SCHEMA, globalStyleDefaults,
} from "@/lib/visualBuilder/registry";
import ProductCardEditor from "@/components/visualBuilder/ProductCardEditor";
import CategoryLayoutEditor from "@/components/visualBuilder/CategoryLayoutEditor";
import HomepageSectionsEditor from "@/components/visualBuilder/HomepageSectionsEditor";
import NavigatorPanel from "@/components/visualBuilder/NavigatorPanel";
import TreeNavigator from "@/components/visualBuilder/TreeNavigator";
import TreeNodeEditor from "@/components/visualBuilder/TreeNodeEditor";
import { migrateBlobToTree, updateNodeSettings, findNode, isWidget as isTreeWidget } from "@/lib/visualBuilder/treeSchema";
import PublishBar from "@/components/visualBuilder/PublishBar";
import { PRODUCT_CARD_DEFAULTS } from "@/lib/visualBuilder/productCardSchema";
import { postPreviewUpdate, withPreviewFlag } from "@/lib/visualBuilder/previewBridge";

const DEVICES = {
  desktop: { label: "Desktop", icon: Monitor,    w: "100%",  h: "72vh" },
  tablet:  { label: "Tablet",  icon: Tablet,     w: "820px", h: "820px" },
  mobile:  { label: "Mobile",  icon: Smartphone, w: "390px", h: "780px" },
};

// Real live-preview URLs per platform (Phase 9). Every consumer reads
// its `ui` blob from /ui-customization and the postMessage bridge in
// `previewBridge.js` merges the current in-progress blob into memory
// without any network write. Publish is the only path to production.
const PREVIEW_URL_BY_PLATFORM = {
  website:      "/",
  customer_app: "/",              // customer web mirror doubles as the App preview
  store_admin:  "/admin/dashboard",
  pos:          "/admin/pos",
};
const previewUrlFor = (p) => PREVIEW_URL_BY_PLATFORM[p] || "/";

// Popular lucide icons pre-baked for the icon picker to keep the panel
// small. Users type any lucide name to search across the full set.
const ICON_PRESETS = [
  "Search", "ShoppingBag", "ShoppingCart", "User", "Users", "Heart",
  "Bell", "Star", "MapPin", "Home", "Package", "Truck", "Wallet",
  "CreditCard", "IndianRupee", "Percent", "BadgePercent", "Zap",
  "Sparkles", "TrendingUp", "BarChart3", "AlertTriangle", "BookOpen",
  "Receipt", "ScanLine", "Camera", "Image", "Tag", "Gift",
];

export default function VisualBuilder() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center text-slate-600" data-testid="vb-unauthorized">
        Super Admin access required.
      </div>
    );
  }
  return <VisualBuilderInner />;
}

function VisualBuilderInner() {
  // ── Target state ──────────────────────────────────────────────────
  const [platform, setPlatform] = useState("website");
  const [alsoApplyTo, setAlsoApplyTo] = useState(new Set());

  // Store scope
  const [scopeMode, setScopeMode] = useState("all");
  const [stores, setStores] = useState([]);
  const [selectedStoreIds, setSelectedStoreIds] = useState(new Set());
  const [storeSearch, setStoreSearch] = useState("");

  // ── Editor state ──────────────────────────────────────────────────
  const emptyBlob = () => ({
    sections: [],
    components: {},
    global_style: {},
    // Legacy consumer blobs that must survive round-trips so existing
    // WebsiteBuilder / customer app keeps rendering while we edit here.
    card: {},
    homepage: {},
    header: {},
    pdp: {},
  });
  const [blob, setBlob] = useState(emptyBlob());
  const [selection, setSelection] = useState(null); // "__global" | compId | null
  const [device, setDevice] = useState("desktop");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [hasServerDraft, setHasServerDraft] = useState(false);

  // Undo/redo history
  const [history, setHistory] = useState([emptyBlob()]);
  const [hIdx, setHIdx] = useState(0);

  // Track saved snapshot for a "dirty" indicator
  const savedRef = useRef(JSON.stringify(emptyBlob()));
  const dirty = JSON.stringify(blob) !== savedRef.current;

  const iframeRef = useRef(null);

  // ── Load stores once ──────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/stores?limit=100");
        setStores(data.items || []);
      } catch (e) {
        toast.error(errMsg(e));
      }
    })();
  }, []);

  // ── Reload blob whenever target platform/scope changes ────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const storeIdForLoad =
          scopeMode === "selected" && selectedStoreIds.size === 1
            ? Array.from(selectedStoreIds)[0]
            : null;
        // First fetch the DRAFT (?draft=true) so unpublished edits show.
        // Fetch revisions meta in parallel to detect if a draft exists.
        const params = new URLSearchParams({ platform, draft: "true" });
        if (storeIdForLoad) params.set("store_id", storeIdForLoad);
        const metaParams = new URLSearchParams({ platform });
        if (storeIdForLoad) metaParams.set("store_id", storeIdForLoad);
        const [{ data }, meta] = await Promise.all([
          api.get(`/ui-customization?${params.toString()}`),
          api.get(`/ui-customization/revisions?${metaParams.toString()}`).catch(() => ({ data: { has_draft: false } })),
        ]);
        if (!alive) return;
        const raw = {
          sections:     Array.isArray(data?.sections) ? data.sections : [],
          components:   data?.components || {},
          global_style: data?.global_style || {},
          // Legacy blobs — preserve so we don't wipe them on PATCH merge.
          card:     data?.card     || {},
          homepage: data?.homepage || {},
          header:   data?.header   || {},
          pdp:      data?.pdp      || {},
          // POS-specific global product-result card config (kept separate
          // from the customer `card` blob so POS design never bleeds
          // into the customer app and vice versa).
          pos_card: data?.pos_card || {},
          // Nested tree schema (Elementor). Migration is idempotent —
          // it's a no-op when a tree already exists.
          tree:            Array.isArray(data?.tree) ? data.tree : [],
          schema_version:  Number(data?.schema_version || 0),
        };
        const next = migrateBlobToTree(raw);
        setBlob(next);
        setHistory([next]);
        setHIdx(0);
        setHasServerDraft(!!meta?.data?.has_draft);
        savedRef.current = JSON.stringify(next);
        setSelection(null);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [platform, scopeMode, selectedStoreIds]);

  // ── History helpers ───────────────────────────────────────────────
  const pushHistory = (next) => {
    setHistory((prev) => {
      const trimmed = prev.slice(0, hIdx + 1);
      trimmed.push(next);
      return trimmed.length > 40 ? trimmed.slice(trimmed.length - 40) : trimmed;
    });
    setHIdx((i) => Math.min(i + 1, 39));
  };
  const commit = (updater) => {
    setBlob((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pushHistory(next);
      return next;
    });
  };
  const undo = () => {
    if (hIdx === 0) return;
    setHIdx(hIdx - 1);
    setBlob(history[hIdx - 1]);
  };
  const redo = () => {
    if (hIdx >= history.length - 1) return;
    setHIdx(hIdx + 1);
    setBlob(history[hIdx + 1]);
  };
  const resetLocal = () => {
    if (!window.confirm("Reset unsaved changes for this target?")) return;
    const clean = JSON.parse(savedRef.current);
    setBlob(clean);
    setHistory([clean]);
    setHIdx(0);
    setSelection(null);
  };
  const canUndo = hIdx > 0;
  const canRedo = hIdx < history.length - 1;

  // ── Sections helpers (drag-drop from palette + reorder) ───────────
  const sections = blob.sections || [];
  const isPlaced = (compId) => sections.some((s) => s.comp === compId);
  const addToCanvas = (compId) => {
    if (isPlaced(compId)) { setSelection(compId); return; }
    commit((prev) => ({
      ...prev,
      sections: [...(prev.sections || []), { id: `s-${Date.now()}-${compId}`, comp: compId }],
    }));
    setSelection(compId);
  };
  const removeFromCanvas = (compId) => {
    commit((prev) => ({
      ...prev,
      sections: (prev.sections || []).filter((s) => s.comp !== compId),
    }));
    if (selection === compId) setSelection(null);
  };
  const reorderCanvas = (fromCompId, toCompId) => {
    if (fromCompId === toCompId) return;
    commit((prev) => {
      const list = [...(prev.sections || [])];
      const from = list.findIndex((s) => s.comp === fromCompId);
      const to = list.findIndex((s) => s.comp === toCompId);
      if (from < 0 || to < 0) return prev;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      return { ...prev, sections: list };
    });
  };
  const moveInCanvas = (compId, delta) => commit((prev) => {
    const list = [...(prev.sections || [])];
    const idx = list.findIndex((s) => s.comp === compId);
    const target = idx + delta;
    if (idx < 0 || target < 0 || target >= list.length) return prev;
    [list[idx], list[target]] = [list[target], list[idx]];
    return { ...prev, sections: list };
  });

  // ── Component settings mutation ───────────────────────────────────
  const compSettings = (compId) => blob.components?.[compId] || {};
  const updateCompSetting = (compId, patch) => commit((prev) => ({
    ...prev,
    components: {
      ...(prev.components || {}),
      [compId]: { ...(prev.components?.[compId] || {}), ...patch },
    },
  }));

  // ── Global style ──────────────────────────────────────────────────
  const globalStyle = { ...globalStyleDefaults(), ...(blob.global_style || {}) };
  const updateGlobalStyle = (patch) => commit((prev) => ({
    ...prev,
    global_style: { ...(prev.global_style || {}), ...patch },
  }));

  // ── Product Card blob (legacy top-level `card` key on the /ui blob) ──
  const cardBlob = blob.card || {};
  const updateCard = (patch) => commit((prev) => ({
    ...prev,
    card: { ...(prev.card || {}), ...patch },
  }));
  const resetCard = () => commit((prev) => ({ ...prev, card: { ...PRODUCT_CARD_DEFAULTS } }));

  // ── Live preview postMessage broadcast (Phase 9) ─────────────────
  // Whenever the current blob changes, post it to the preview iframe so
  // the target site inside the canvas updates instantly WITHOUT any
  // network write. Fires for every platform — the consumer decides
  // whether to react via `subscribePreview()` in previewBridge.js.
  useEffect(() => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    postPreviewUpdate(win, platform, blob);
  }, [blob, platform]);

  // Re-broadcast the current blob when the iframe finishes loading — the
  // consumer wasn't ready to listen while it was booting.
  useEffect(() => {
    const el = iframeRef.current;
    if (!el) return;
    const onLoad = () => postPreviewUpdate(el.contentWindow, platform, blob);
    el.addEventListener("load", onLoad);
    return () => el.removeEventListener("load", onLoad);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform]);

  // ── Save & Apply — N × M parallel PATCH for isolation ─────────────
  const targetPlatforms = useMemo(() => Array.from(new Set([platform, ...alsoApplyTo])), [platform, alsoApplyTo]);
  const targetStoreIds = useMemo(() => scopeMode === "all" ? [null] : Array.from(selectedStoreIds), [scopeMode, selectedStoreIds]);

  const runWrites = async (mode) => {
    // mode: "draft" | "publish" | "legacy"
    if (targetStoreIds.length === 0) {
      toast.error("Pick at least one store, or switch to All Stores.");
      return false;
    }
    const jobs = [];
    for (const p of targetPlatforms) {
      for (const sid of targetStoreIds) {
        const params = new URLSearchParams({ platform: p });
        if (sid) params.set("store_id", sid);
        if (mode === "draft") params.set("draft", "true");
        // "legacy" mode leaves both flags off → server auto-publishes.
        jobs.push(api.patch(`/ui-customization?${params.toString()}`, blob));
      }
    }
    await Promise.all(jobs);
    return true;
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const ok = await runWrites("draft");
      if (!ok) return;
      savedRef.current = JSON.stringify(blob);
      setHasServerDraft(true);
      toast.success("Draft saved — customers won't see changes until you Publish.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      // Save current dirty state to draft first (idempotent — server merges).
      if (JSON.stringify(blob) !== savedRef.current) {
        const ok = await runWrites("draft");
        if (!ok) return;
      }
      // Then explicitly promote draft → published for every target.
      const jobs = [];
      for (const p of targetPlatforms) {
        for (const sid of targetStoreIds) {
          const params = new URLSearchParams({ platform: p });
          if (sid) params.set("store_id", sid);
          jobs.push(api.post(`/ui-customization/publish?${params.toString()}`));
        }
      }
      await Promise.all(jobs);
      savedRef.current = JSON.stringify(blob);
      setHasServerDraft(false);
      toast.success(`Published to ${targetPlatforms.length} platform${targetPlatforms.length > 1 ? "s" : ""} × ${targetStoreIds.length === 1 && targetStoreIds[0] === null ? "All stores" : `${targetStoreIds.length} store(s)`}`);
      if (iframeRef.current) {
        try { iframeRef.current.src = iframeRef.current.src; } catch { /* noop */ }
      }
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPublishing(false); }
  };

  const discardDraft = async () => {
    if (!window.confirm("Discard the current unpublished draft? This cannot be undone.")) return;
    setReverting(true);
    try {
      const jobs = [];
      for (const p of targetPlatforms) {
        for (const sid of targetStoreIds) {
          const params = new URLSearchParams({ platform: p });
          if (sid) params.set("store_id", sid);
          jobs.push(api.post(`/ui-customization/revert?${params.toString()}`));
        }
      }
      await Promise.all(jobs);
      // Reload published blob into the editor.
      const params = new URLSearchParams({ platform });
      if (scopeMode === "selected" && selectedStoreIds.size === 1) {
        params.set("store_id", Array.from(selectedStoreIds)[0]);
      }
      const { data } = await api.get(`/ui-customization?${params.toString()}`);
      const next = {
        sections: Array.isArray(data?.sections) ? data.sections : [],
        components: data?.components || {},
        global_style: data?.global_style || {},
        card: data?.card || {}, homepage: data?.homepage || {}, header: data?.header || {}, pdp: data?.pdp || {},
        pos_card: data?.pos_card || {},
        tree: Array.isArray(data?.tree) ? data.tree : [], schema_version: Number(data?.schema_version || 0),
      };
      setBlob(next); setHistory([next]); setHIdx(0);
      setHasServerDraft(false);
      savedRef.current = JSON.stringify(next);
      toast.success("Draft discarded — reverted to last published version.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setReverting(false); }
  };

  // Legacy one-shot save-and-publish (for the "Apply Now" hidden action)
  const saveAll = async () => {
    setSaving(true);
    try {
      const ok = await runWrites("legacy");
      if (!ok) return;
      savedRef.current = JSON.stringify(blob);
      setHasServerDraft(false);
      const total = targetPlatforms.length * targetStoreIds.length;
      toast.success(
        `Applied to ${targetPlatforms.length} platform${targetPlatforms.length > 1 ? "s" : ""}` +
        ` × ${targetStoreIds.length === 1 && targetStoreIds[0] === null ? "All stores" : `${targetStoreIds.length} store(s)`}` +
        ` (${total} write${total > 1 ? "s" : ""})`,
      );
      if (iframeRef.current) {
        try { iframeRef.current.src = iframeRef.current.src; } catch { /* noop */ }
      }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  // ── Palette groups (platform-aware) ───────────────────────────────
  const groups = useMemo(() => groupsFor(platform), [platform]);
  const platformMeta = PLATFORMS.find((p) => p.id === platform);

  // ── DnD state ─────────────────────────────────────────────────────
  const [dragSource, setDragSource] = useState(null); // { type: "palette" | "canvas", compId }
  const [dragOverComp, setDragOverComp] = useState(null);
  const [canvasHot, setCanvasHot] = useState(false);

  const onPaletteDragStart = (compId) => (e) => {
    setDragSource({ type: "palette", compId });
    try { e.dataTransfer.effectAllowed = "copy"; e.dataTransfer.setData("text/plain", compId); } catch {/*noop*/}
  };
  const onCanvasDragStart = (compId) => (e) => {
    setDragSource({ type: "canvas", compId });
    try { e.dataTransfer.effectAllowed = "move"; } catch {/*noop*/}
  };
  const onCanvasDragOver = (e) => {
    e.preventDefault();
    if (!canvasHot) setCanvasHot(true);
    try { e.dataTransfer.dropEffect = dragSource?.type === "canvas" ? "move" : "copy"; } catch {/*noop*/}
  };
  const onCardDragOver = (compId) => (e) => {
    e.preventDefault();
    if (dragOverComp !== compId) setDragOverComp(compId);
    try { e.dataTransfer.dropEffect = dragSource?.type === "canvas" ? "move" : "copy"; } catch {/*noop*/}
  };
  const onCardDrop = (compId) => (e) => {
    e.preventDefault();
    if (!dragSource) return;
    if (dragSource.type === "palette") {
      // Insert BEFORE the hovered card
      commit((prev) => {
        const list = [...(prev.sections || [])];
        if (list.some((s) => s.comp === dragSource.compId)) return prev;
        const idx = list.findIndex((s) => s.comp === compId);
        const entry = { id: `s-${Date.now()}-${dragSource.compId}`, comp: dragSource.compId };
        if (idx < 0) list.push(entry); else list.splice(idx, 0, entry);
        return { ...prev, sections: list };
      });
      setSelection(dragSource.compId);
    } else if (dragSource.type === "canvas") {
      reorderCanvas(dragSource.compId, compId);
    }
    setDragSource(null); setDragOverComp(null); setCanvasHot(false);
  };
  const onCanvasDrop = (e) => {
    e.preventDefault();
    if (!dragSource) return;
    if (dragSource.type === "palette") addToCanvas(dragSource.compId);
    setDragSource(null); setDragOverComp(null); setCanvasHot(false);
  };
  const onDragEnd = () => { setDragSource(null); setDragOverComp(null); setCanvasHot(false); };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="visual-builder-page">
      {/* ─── Sticky action bar ───────────────────────────────────── */}
      <ActionBar
        platform={platform} setPlatform={(p) => { setPlatform(p); setAlsoApplyTo(new Set()); }}
        canUndo={canUndo} canRedo={canRedo} onUndo={undo} onRedo={redo} onReset={resetLocal}
        device={device} setDevice={setDevice}
        saving={saving} publishing={publishing} reverting={reverting}
        dirty={dirty} hasServerDraft={hasServerDraft}
        onSaveDraft={saveDraft} onPublish={publish} onDiscardDraft={discardDraft} onSaveAndApply={saveAll}
      />

      {/* Apply-to strip */}
      <div className="bg-white border-b border-slate-100 px-4 md:px-6 py-2.5 flex items-center gap-2 flex-wrap text-[12px] sticky top-[60px] z-20" data-testid="vb-apply-to">
        <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
          <TargetIcon className="w-3.5 h-3.5" /> Apply to:
        </div>
        <div className="inline-flex items-center gap-1 flex-wrap">
          <Chip active label={platformMeta?.label || platform} testId="vb-apply-primary" />
          {PLATFORMS.filter((p) => p.id !== platform).map((p) => {
            const on = alsoApplyTo.has(p.id);
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setAlsoApplyTo((prev) => {
                  const s = new Set(prev); s.has(p.id) ? s.delete(p.id) : s.add(p.id); return s;
                })}
                data-testid={`vb-also-${p.id}`}
              >
                <Chip active={on} label={`+ ${p.label}`} muted={!on} />
              </button>
            );
          })}
        </div>

        <div className="hidden md:block h-4 w-px bg-slate-200 mx-2" />

        <div className="flex items-center gap-1.5 text-slate-500 font-semibold">
          <Store className="w-3.5 h-3.5" /> Stores:
        </div>
        <div className="inline-flex items-center gap-1 rounded-lg bg-slate-100 p-1">
          <button
            type="button"
            onClick={() => setScopeMode("all")}
            className={`h-7 px-2.5 rounded-md text-[12px] font-semibold ${scopeMode === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid="vb-scope-all"
          >
            All Stores
          </button>
          <button
            type="button"
            onClick={() => setScopeMode("selected")}
            className={`h-7 px-2.5 rounded-md text-[12px] font-semibold ${scopeMode === "selected" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
            data-testid="vb-scope-selected"
          >
            Selected {selectedStoreIds.size > 0 && `(${selectedStoreIds.size})`}
          </button>
        </div>
        {scopeMode === "selected" && (
          <StorePicker
            stores={stores}
            selected={selectedStoreIds}
            onToggle={(id) => setSelectedStoreIds((prev) => {
              const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
            })}
            search={storeSearch} setSearch={setStoreSearch}
          />
        )}
      </div>

      {/* ─── 3-column grid ───────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr_340px] min-h-[calc(100vh-160px)]">
        {/* LEFT — palette */}
        <aside className="bg-white border-r border-slate-100 overflow-y-auto" data-testid="vb-left-pane">
          {/* Global Style trigger */}
          <button
            type="button"
            onClick={() => setSelection("__global")}
            className={`m-3 w-[calc(100%-24px)] flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all ${
              selection === "__global"
                ? "border-[#E32636] bg-[#FFF3F4] text-[#E32636] shadow-sm"
                : "border-slate-200 bg-white hover:border-slate-300 text-slate-700"
            }`}
            data-testid="vb-open-global-style"
          >
            <Palette className="w-4 h-4" />
            <span className="text-[13px] font-semibold flex-1 text-left">Global Style</span>
            <ChevronRight className="w-3.5 h-3.5 opacity-60" />
          </button>

          <div className="px-4 py-2 flex items-center gap-2">
            <Layers className="w-4 h-4 text-slate-500" />
            <div className="text-[13px] font-semibold text-slate-900">Components</div>
            <span className="ml-auto text-[10px] font-mono text-slate-400 uppercase">
              {platformMeta?.label}
            </span>
          </div>
          <div className="px-3 pb-3 space-y-3">
            {groups.map((g) => (
              <div key={g.label}>
                <div className="px-1 py-1 text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                  {g.label}
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  {g.items.map((cc) => {
                    const placed = isPlaced(cc.id);
                    return (
                      <div
                        key={cc.id}
                        draggable
                        onDragStart={onPaletteDragStart(cc.id)}
                        onDragEnd={onDragEnd}
                        onClick={() => (placed ? setSelection(cc.id) : addToCanvas(cc.id))}
                        className={`group cursor-grab active:cursor-grabbing flex flex-col items-center gap-1.5 h-16 rounded-xl border transition-all p-2 ${
                          placed
                            ? "border-slate-300 bg-slate-100"
                            : "border-slate-200 bg-white hover:border-[#E32636] hover:bg-[#FFF3F4]"
                        }`}
                        data-testid={`vb-comp-${cc.id}`}
                        title={placed ? "Placed — click to edit" : "Drag or click to add"}
                      >
                        <div className={`w-6 h-6 rounded-md grid place-items-center text-[10px] font-bold ${
                          placed ? "bg-slate-300 text-slate-700" : "bg-slate-100 text-slate-600 group-hover:bg-[#E32636] group-hover:text-white"
                        }`}>
                          {placed ? <Check className="w-3.5 h-3.5" /> : cc.label.charAt(0)}
                        </div>
                        <div className="text-[10.5px] font-semibold text-center leading-tight text-slate-700 line-clamp-1">
                          {cc.label}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Elementor-style nested Tree navigator (Section → Row →
              Column → Widget). Migration from the flat `sections[]` is
              idempotent so this is safe to render at all times. */}
          <TreeNavigator
            tree={blob.tree || []}
            selection={typeof selection === "string" && selection.startsWith("tree:") ? selection.slice(5) : null}
            onSelect={(node) => setSelection(`tree:${node.id}`)}
            onChange={(patch) => commit((prev) => ({ ...prev, ...patch }))}
          />

          {/* Legacy Navigator — flat homepage sections + canvas widgets.
              Kept alongside the tree until the customer runtime fully
              consumes the tree. */}
          <NavigatorPanel
            platform={platform}
            blob={blob}
            selection={selection}
            onSelect={setSelection}
            onChange={(patch) => commit((prev) => ({ ...prev, ...patch }))}
          />
        </aside>

        {/* CENTER — canvas */}
        <section className="bg-slate-50 flex flex-col items-center py-5" data-testid="vb-canvas-pane">
          <div className="text-[11px] text-slate-500 font-mono mb-3">
            {DEVICES[device].label} · {DEVICES[device].w}
          </div>
          <div
            className={`bg-white rounded-2xl shadow-[0_20px_60px_-24px_rgba(15,23,42,0.25)] border ${canvasHot ? "border-[#E32636] ring-2 ring-[#E32636]/20" : "border-slate-200"} overflow-hidden transition-all duration-300`}
            style={{
              width: device === "desktop" ? "100%" : DEVICES[device].w,
              maxWidth: device === "desktop" ? "1180px" : DEVICES[device].w,
              height: DEVICES[device].h,
            }}
            data-testid={`vb-canvas-frame-${device}`}
            onDragOver={onCanvasDragOver}
            onDragLeave={() => setCanvasHot(false)}
            onDrop={onCanvasDrop}
          >
            <iframe
              ref={iframeRef}
              key={platform /* force reload when platform changes */}
              src={withPreviewFlag(previewUrlFor(platform))}
              title={`${platformMeta?.label} preview`}
              className="w-full h-full border-0 bg-white"
              data-testid="vb-canvas-iframe"
            />
          </div>
          {platform !== "website" && (
            <div className="mt-3 text-[11px] text-slate-500 max-w-md text-center flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              Live preview of the real {platformMeta?.label} — edits stream in via postMessage, Publish makes them permanent.
            </div>
          )}
        </section>

        {/* RIGHT — settings */}
        <aside className="border-l border-slate-100 bg-white overflow-y-auto" data-testid="vb-right-pane">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400 text-[13px]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : selection === "__global" ? (
            <GlobalStylePanel
              values={globalStyle}
              onChange={updateGlobalStyle}
              onClose={() => setSelection(null)}
            />
          ) : selection && typeof selection === "string" && selection.startsWith("tree:") ? (
            <TreeNodeEditor
              tree={blob.tree || []}
              nodeId={selection.slice(5)}
              onChange={(patch) => commit((prev) => ({ ...prev, ...patch }))}
              onClose={() => setSelection(null)}
            />
          ) : selection && componentById(platform, selection)?.editor === "product_card" ? (
            <ProductCardEditor
              card={cardBlob}
              onChange={updateCard}
              onReset={resetCard}
              onClose={() => setSelection(null)}
            />
          ) : selection && componentById(platform, selection)?.editor === "pos_card" ? (
            <ProductCardEditor
              card={blob.pos_card || {}}
              onChange={(patch) => commit((prev) => ({ ...prev, pos_card: { ...(prev.pos_card || {}), ...patch } }))}
              onReset={() => commit((prev) => ({ ...prev, pos_card: { ...PRODUCT_CARD_DEFAULTS } }))}
              onClose={() => setSelection(null)}
            />
          ) : selection && componentById(platform, selection)?.editor === "category_layout" ? (
            <CategoryLayoutEditor
              compId={selection}
              compLabel={componentById(platform, selection)?.label}
              values={compSettings(selection)}
              onChange={(patch) => updateCompSetting(selection, patch)}
              onClose={() => setSelection(null)}
            />
          ) : selection && componentById(platform, selection)?.editor === "homepage_sections" ? (
            <HomepageSectionsEditor
              homepage={blob.homepage || {}}
              baseCard={blob.card || {}}
              onChange={(patch) => commit((prev) => ({ ...prev, ...patch }))}
              onClose={() => setSelection(null)}
            />
          ) : selection ? (
            <SettingsPanel
              key={`${platform}-${selection}`}
              platform={platformMeta}
              comp={componentById(platform, selection)}
              values={compSettings(selection)}
              onChange={(patch) => updateCompSetting(selection, patch)}
              onClose={() => setSelection(null)}
              onRemove={() => removeFromCanvas(selection)}
              isPlaced={isPlaced(selection)}
            />
          ) : (
            <EmptyRightPane onOpenGlobal={() => setSelection("__global")} />
          )}
        </aside>
      </div>
    </div>
  );
}

/* ─── Sticky action bar ────────────────────────────────────────────── */

function ActionBar({
  platform, setPlatform, canUndo, canRedo, onUndo, onRedo, onReset,
  device, setDevice, saving, publishing, reverting, dirty, hasServerDraft,
  onSaveDraft, onPublish, onDiscardDraft, onSaveAndApply,
}) {
  return (
    <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <div className="w-9 h-9 rounded-xl bg-[#E32636] text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold leading-none">
            Super Admin · App Management
          </div>
          <div className="text-[15px] font-semibold text-slate-900 leading-tight">Visual Builder</div>
        </div>
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 ml-1 md:ml-4" data-testid="vb-target-selector">
        {PLATFORMS.map((p) => {
          const active = platform === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPlatform(p.id)}
              className={`h-8 px-3 rounded-lg text-[12px] font-semibold transition-all ${
                active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`vb-target-${p.id}`}
              title={p.hint}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-1 ml-1">
        <IconBtn onClick={onUndo} disabled={!canUndo} title="Undo" testId="vb-undo"><Undo2 className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={onRedo} disabled={!canRedo} title="Redo" testId="vb-redo"><Redo2 className="w-4 h-4" /></IconBtn>
        <IconBtn onClick={onReset} title="Reset unsaved" testId="vb-reset"><RotateCcw className="w-4 h-4" /></IconBtn>
      </div>

      <div className="mx-auto flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="vb-device-switcher">
        {Object.entries(DEVICES).map(([k, cfg]) => {
          const active = device === k;
          const Icon = cfg.icon;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setDevice(k)}
              className={`h-8 px-2.5 md:px-3 rounded-lg flex items-center gap-1.5 text-[12px] font-semibold transition-all ${
                active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`vb-device-${k}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="hidden md:inline">{cfg.label}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onSaveAndApply}
        disabled={saving || publishing}
        className="hidden"
        aria-hidden
      />
      <PublishBar
        saving={saving} publishing={publishing} reverting={reverting}
        dirty={dirty} hasServerDraft={hasServerDraft}
        onSaveDraft={onSaveDraft} onPublish={onPublish}
        onDiscardDraft={onDiscardDraft} onSaveAndApply={onSaveAndApply}
      />
    </div>
  );
}

function IconBtn({ children, onClick, disabled, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="w-9 h-9 grid place-items-center rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function Chip({ active, label, muted, testId }) {
  return (
    <span
      className={`inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-[11.5px] font-semibold border transition-all ${
        active && !muted
          ? "bg-[#E32636] text-white border-[#E32636]"
          : muted
          ? "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
          : "bg-slate-900 text-white border-slate-900"
      }`}
      data-testid={testId}
    >
      {active && !muted && <Check className="w-3 h-3" />}
      {label}
    </span>
  );
}

/* ─── Store picker ─────────────────────────────────────────────────── */

function StorePicker({ stores, selected, onToggle, search, setSearch }) {
  const [open, setOpen] = useState(false);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return stores;
    return stores.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [stores, search]);
  return (
    <div className="relative" data-testid="vb-store-picker">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="h-7 px-2.5 rounded-md bg-white border border-slate-200 text-[12px] font-semibold text-slate-700 hover:border-slate-300 inline-flex items-center gap-1"
        data-testid="vb-store-picker-toggle"
      >
        Pick stores <ChevronRight className={`w-3 h-3 transition ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-40 top-9 left-0 w-72 bg-white rounded-xl border border-slate-200 shadow-lg p-2" data-testid="vb-store-picker-panel">
          <div className="relative">
            <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search store…" className="h-8 pl-7 text-[12px]" data-testid="vb-store-picker-search" />
          </div>
          <div className="max-h-64 overflow-y-auto mt-1.5">
            {filtered.length === 0 ? (
              <div className="text-center text-[11.5px] text-slate-400 py-4">No stores</div>
            ) : filtered.map((s) => {
              const on = selected.has(s.id);
              return (
                <button key={s.id} type="button" onClick={() => onToggle(s.id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[12.5px] ${
                    on ? "bg-[#FFF3F4] text-[#E32636] font-semibold" : "hover:bg-slate-50 text-slate-700"
                  }`}
                  data-testid={`vb-store-opt-${s.id}`}
                >
                  <span className={`w-4 h-4 rounded border grid place-items-center ${on ? "border-[#E32636] bg-[#E32636] text-white" : "border-slate-300"}`}>
                    {on && <Check className="w-3 h-3" />}
                  </span>
                  <span className="truncate">{s.name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Canvas preview (non-website) ─────────────────────────────────── */

function CanvasPreview({
  platform, sections, blob, globalStyle, selection, dragOverComp,
  onSelect, onRemove, onMove, onDragStartCard, onDragOverCard, onDropCard, onDragEnd,
}) {
  const cardBg = globalStyle.card_bg || "#ffffff";
  const cardRadius = globalStyle.card_radius ?? 16;
  const bg = globalStyle.bg || "#f8fafc";
  const label = PLATFORMS.find((p) => p.id === platform)?.label || platform;

  return (
    <div className="w-full h-full flex flex-col" style={{ background: bg }} data-testid={`vb-canvas-${platform}`}>
      {sections.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border-2 border-dashed border-slate-300 grid place-items-center">
            <Plus className="w-6 h-6 text-slate-400" />
          </div>
          <div className="text-[13px] font-semibold text-slate-700">Drop components here</div>
          <div className="text-[11.5px] text-slate-500 max-w-[240px]">
            Drag any component from the left palette to compose your {label} layout.
          </div>
        </div>
      ) : (
        <div className="p-4 flex-1 overflow-y-auto space-y-2.5">
          {sections.map((s, idx) => (
            <CanvasCard
              key={s.id}
              index={idx}
              total={sections.length}
              platform={platform}
              compId={s.comp}
              blob={blob}
              cardBg={cardBg}
              cardRadius={cardRadius}
              selected={selection === s.comp}
              dragOver={dragOverComp === s.comp}
              onSelect={() => onSelect(s.comp)}
              onRemove={() => onRemove(s.comp)}
              onMoveUp={() => onMove(s.comp, -1)}
              onMoveDown={() => onMove(s.comp, 1)}
              onDragStart={onDragStartCard(s.comp)}
              onDragOver={onDragOverCard(s.comp)}
              onDrop={onDropCard(s.comp)}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CanvasCard({
  index, total, platform, compId, blob, cardBg, cardRadius, selected, dragOver,
  onSelect, onRemove, onMoveUp, onMoveDown,
  onDragStart, onDragOver, onDrop, onDragEnd,
}) {
  const comp = componentById(platform, compId);
  if (!comp) return null;
  const settings = blob.components?.[compId] || {};
  if (settings.visibility === false) return (
    <div
      className={`rounded-xl border-2 border-dashed p-2 opacity-60 cursor-pointer ${selected ? "border-[#E32636]" : "border-slate-300"}`}
      onClick={onSelect}
      data-testid={`vb-canvas-card-${compId}`}
    >
      <div className="text-[11px] text-slate-400 line-through">{settings.label || comp.label} · hidden</div>
    </div>
  );

  const bg = settings.bg || cardBg;
  const textColor = settings.text_color || null;
  const radius = settings.radius ?? cardRadius;
  const padding = settings.padding ?? 12;
  const margin = settings.margin ?? 0;
  const borderColor = settings.border_color || (selected ? "#E32636" : "transparent");
  const borderWidth = settings.border_width ?? (selected ? 2 : 1);
  const shadow = ({
    none: "none",
    soft: "0 2px 8px -2px rgba(15,23,42,0.10)",
    medium: "0 6px 16px -4px rgba(15,23,42,0.15)",
    strong: "0 20px 40px -12px rgba(15,23,42,0.25)",
  })[settings.shadow || "soft"];
  const align = settings.align || "left";
  const width = settings.width || "full";
  const widthClass = { full: "w-full", half: "w-1/2", third: "w-1/3", auto: "w-auto" }[width] || "w-full";
  const IconEl = settings.icon && LucideIcons[settings.icon];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onClick={onSelect}
      className={`group relative flex items-center ${widthClass} ${dragOver ? "ring-2 ring-[#E32636]/50" : ""}`}
      style={{
        marginLeft: align === "right" ? "auto" : align === "center" ? "auto" : 0,
        marginRight: align === "left" ? "auto" : align === "center" ? "auto" : 0,
        marginTop: margin, marginBottom: margin,
      }}
      data-testid={`vb-canvas-card-${compId}`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
        className="flex-1 text-left cursor-pointer transition-all"
        style={{
          background: bg,
          borderRadius: radius,
          padding,
          border: `${borderWidth}px solid ${borderColor}`,
          boxShadow: shadow,
          color: textColor || undefined,
          textAlign: align,
        }}
      >
        <div className="flex items-center gap-2">
          <GripVertical className="w-3.5 h-3.5 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" />
          {settings.image ? (
            <img src={cldOptimize(settings.image, { width: 80, height: 80, crop: "fill" })} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
          ) : IconEl ? (
            <div className="w-8 h-8 rounded-lg grid place-items-center bg-white/40 shrink-0">
              <IconEl className="w-4 h-4" style={{ color: settings.icon_color || textColor || "currentColor" }} />
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-semibold truncate" style={textColor ? { color: textColor } : undefined}>
              {settings.label || comp.label}
            </div>
            {settings.content && (
              <div className="text-[11px] opacity-70 mt-0.5 line-clamp-2">{settings.content}</div>
            )}
          </div>
        </div>
      </button>

      <div className="absolute -top-2 -right-2 hidden group-hover:flex items-center gap-0.5 bg-white rounded-lg shadow-md border border-slate-200 p-0.5">
        <button type="button" onClick={(e) => { e.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="w-6 h-6 grid place-items-center rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`vb-card-up-${compId}`}>
          <ArrowUp className="w-3 h-3" />
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onMoveDown(); }} disabled={index === total - 1} className="w-6 h-6 grid place-items-center rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" data-testid={`vb-card-down-${compId}`}>
          <ArrowDown className="w-3 h-3" />
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onRemove(); }} className="w-6 h-6 grid place-items-center rounded text-red-500 hover:bg-red-50" data-testid={`vb-card-remove-${compId}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

/* ─── Empty right pane ─────────────────────────────────────────────── */

function EmptyRightPane({ onOpenGlobal }) {
  return (
    <div className="p-6 text-center">
      <div className="w-12 h-12 mx-auto rounded-2xl bg-slate-100 grid place-items-center mb-3">
        <Settings2 className="w-5 h-5 text-slate-400" />
      </div>
      <div className="text-[13px] font-semibold text-slate-700">Pick a component</div>
      <div className="text-[11.5px] text-slate-500 mt-1 mb-4">
        Drag a component into the canvas, or open Global Style to tune the theme tokens shared across the whole target.
      </div>
      <button
        type="button"
        onClick={onOpenGlobal}
        className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-slate-900 text-white text-[12px] font-semibold hover:bg-slate-800"
        data-testid="vb-open-global-style-empty"
      >
        <Palette className="w-3.5 h-3.5" /> Open Global Style
      </button>
    </div>
  );
}

/* ─── Global Style panel ───────────────────────────────────────────── */

function GlobalStylePanel({ values, onChange, onClose }) {
  return (
    <div className="p-5 space-y-6" data-testid="vb-global-style-panel">
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Palette className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Theme Tokens</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">Global Style</div>
          <div className="text-[10.5px] text-slate-400">Applies to every component on this target</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-global-close" title="Close">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <SchemaSection title="Colors" rows={GLOBAL_STYLE_SCHEMA.colors} values={values} onChange={onChange} kind="color" testPrefix="vb-gs-color" />
      <SchemaSection title="Typography" rows={GLOBAL_STYLE_SCHEMA.typography} values={values} onChange={onChange} testPrefix="vb-gs-type" />
      <SchemaSection title="Corner Radius" rows={GLOBAL_STYLE_SCHEMA.radii} values={values} onChange={onChange} testPrefix="vb-gs-radius" />
      <SchemaSection title="Spacing" rows={GLOBAL_STYLE_SCHEMA.spacing} values={values} onChange={onChange} testPrefix="vb-gs-spacing" />

      <div className="pt-3 text-[10.5px] text-slate-400 border-t border-slate-100 leading-relaxed">
        These tokens are consumed by the corresponding platform's theme system.
        Business logic (tax, discount, stock) is never editable.
      </div>
    </div>
  );
}

function SchemaSection({ title, rows, values, onChange, kind, testPrefix }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-widest text-slate-500 font-bold mb-2">{title}</div>
      <div className="grid grid-cols-2 gap-3">
        {rows.map((r) => (
          <Field key={r.key} label={r.label}>
            {kind === "color" ? (
              <ColorInput
                value={values[r.key] ?? r.default}
                onChange={(v) => onChange({ [r.key]: v })}
                testId={`${testPrefix}-${r.key}`}
              />
            ) : r.type === "number" ? (
              <Input
                type="number"
                value={values[r.key] ?? ""}
                onChange={(e) => onChange({ [r.key]: e.target.value === "" ? null : Number(e.target.value) })}
                className="h-9 text-[13px]"
                min={r.min} max={r.max} step={r.step || 1}
                placeholder={String(r.default)}
                data-testid={`${testPrefix}-${r.key}`}
              />
            ) : (
              <Input
                value={values[r.key] ?? ""}
                onChange={(e) => onChange({ [r.key]: e.target.value })}
                className="h-9 text-[12px]"
                placeholder={r.default}
                data-testid={`${testPrefix}-${r.key}`}
              />
            )}
          </Field>
        ))}
      </div>
    </div>
  );
}

/* ─── Component settings panel — safe UI-only fields ──────────────── */

function SettingsPanel({ platform, comp, values, onChange, onClose, onRemove, isPlaced }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [iconQuery, setIconQuery] = useState("");

  const filteredIcons = useMemo(() => {
    const q = iconQuery.trim().toLowerCase();
    if (!q) return ICON_PRESETS;
    const preset = ICON_PRESETS.filter((n) => n.toLowerCase().includes(q));
    if (preset.length) return preset;
    return Object.keys(LucideIcons).filter((n) => /^[A-Z]/.test(n) && n.toLowerCase().includes(q)).slice(0, 30);
  }, [iconQuery]);

  if (!comp) return null;
  const s = comp.supports;

  const onImageFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await uploadToCloudinary(file, { folder: "visual-builder" });
      onChange({ image: url });
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setUploading(false); }
  };

  return (
    <div className="p-5 space-y-5" data-testid={`vb-settings-${comp.id}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-xl bg-slate-900 text-white grid place-items-center shrink-0">
          <Settings2 className="w-4 h-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">{platform?.label} · Component</div>
          <div className="text-[15px] font-semibold text-slate-900 truncate">{values.label || comp.label}</div>
          <div className="text-[10.5px] font-mono text-slate-400 truncate">{comp.id}</div>
        </div>
        <button type="button" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100" data-testid="vb-settings-close" title="Close"><X className="w-3.5 h-3.5" /></button>
      </div>

      {!isPlaced && (
        <div className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Not placed on canvas yet — settings will apply once you drop it in.
        </div>
      )}

      {/* Visibility */}
      <Field label="Visibility" hint="Hide from the live UI without deleting.">
        <div className="flex items-center gap-3">
          <Switch checked={values.visibility !== false} onCheckedChange={(v) => onChange({ visibility: v })} data-testid="vb-set-visibility" />
          <span className={`text-[12px] font-semibold ${values.visibility === false ? "text-slate-400" : "text-emerald-600"}`}>
            {values.visibility === false ? "Hidden" : "Live"}
          </span>
        </div>
      </Field>

      {/* Content: label */}
      {s.content && (
        <>
          <Field label="Label" hint="Override the component's display label.">
            <Input value={values.label ?? ""} onChange={(e) => onChange({ label: e.target.value })} placeholder={comp.label} className="h-9 text-[13px]" data-testid="vb-set-label" />
          </Field>
          <Field label="Description">
            <textarea
              value={values.content ?? ""}
              onChange={(e) => onChange({ content: e.target.value })}
              placeholder="Optional description / body text"
              className="w-full min-h-[64px] text-[13px] rounded-lg border border-slate-200 px-3 py-2 focus:border-slate-400 focus:outline-none"
              data-testid="vb-set-content"
            />
          </Field>
        </>
      )}

      {/* Image */}
      {s.image && (
        <Field label="Image">
          <div className="flex items-center gap-3">
            {values.image ? (
              <div className="relative">
                <img src={cldOptimize(values.image, { width: 160, height: 160, crop: "fill" })} alt="" className="w-16 h-16 rounded-lg object-cover border border-slate-200" data-testid="vb-set-image-thumb" />
                <button type="button" onClick={() => onChange({ image: "" })} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-slate-200 grid place-items-center text-red-500 hover:bg-red-50 shadow-sm" data-testid="vb-set-image-remove"><X className="w-3 h-3" /></button>
              </div>
            ) : (
              <div className="w-16 h-16 rounded-lg border-2 border-dashed border-slate-300 grid place-items-center text-slate-400"><ImageIcon className="w-5 h-5" /></div>
            )}
            <div className="flex-1">
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onImageFile(e.target.files?.[0])} data-testid="vb-set-image-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-700 hover:border-slate-300 disabled:opacity-60" data-testid="vb-set-image-upload">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
                {uploading ? "Uploading…" : values.image ? "Replace" : "Upload"}
              </button>
              <div className="text-[10.5px] text-slate-400 mt-1">Existing Cloudinary CDN · auto-optimised.</div>
            </div>
          </div>
        </Field>
      )}

      {/* Icon */}
      {s.icon && (
        <Field label="Icon">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg bg-slate-100 grid place-items-center" data-testid="vb-set-icon-preview">
                {values.icon && LucideIcons[values.icon]
                  ? (() => { const I = LucideIcons[values.icon]; return <I className="w-4 h-4" style={{ color: values.icon_color || undefined }} />; })()
                  : <span className="text-[10px] text-slate-400">none</span>}
              </div>
              <Input value={iconQuery} onChange={(e) => setIconQuery(e.target.value)} placeholder="Search lucide icons…" className="h-9 text-[12px] flex-1" data-testid="vb-set-icon-search" />
              {values.icon && (
                <button type="button" onClick={() => onChange({ icon: "" })} className="text-[11px] text-slate-500 hover:text-red-500 px-2" data-testid="vb-set-icon-clear">Clear</button>
              )}
            </div>
            <div className="grid grid-cols-6 gap-1 max-h-40 overflow-y-auto">
              {filteredIcons.map((name) => {
                const I = LucideIcons[name];
                if (!I) return null;
                const active = values.icon === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => onChange({ icon: name })}
                    className={`h-8 rounded-md grid place-items-center border ${active ? "border-[#E32636] bg-[#FFF3F4] text-[#E32636]" : "border-slate-200 hover:border-slate-300 text-slate-600"}`}
                    title={name}
                    data-testid={`vb-set-icon-${name}`}
                  >
                    <I className="w-3.5 h-3.5" />
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Icon color">
                <ColorInput value={values.icon_color ?? ""} onChange={(v) => onChange({ icon_color: v })} testId="vb-set-icon-color" />
              </Field>
            </div>
          </div>
        </Field>
      )}

      {/* Colors */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Background"><ColorInput value={values.bg ?? ""} onChange={(v) => onChange({ bg: v })} testId="vb-set-bg" /></Field>
        <Field label="Text color"><ColorInput value={values.text_color ?? ""} onChange={(v) => onChange({ text_color: v })} testId="vb-set-text-color" /></Field>
      </div>

      {/* Border */}
      {s.border && (
        <div className="grid grid-cols-2 gap-3">
          <Field label="Border color"><ColorInput value={values.border_color ?? ""} onChange={(v) => onChange({ border_color: v })} testId="vb-set-border-color" /></Field>
          <Field label="Border width (px)">
            <Input type="number" min={0} max={12} value={values.border_width ?? ""} onChange={(e) => onChange({ border_width: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 text-[13px]" placeholder="0" data-testid="vb-set-border-width" />
          </Field>
        </div>
      )}

      {/* Radius / Padding */}
      <div className="grid grid-cols-2 gap-3">
        <Field label="Radius (px)"><Input type="number" min={0} max={48} value={values.radius ?? ""} onChange={(e) => onChange({ radius: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 text-[13px]" placeholder="Global" data-testid="vb-set-radius" /></Field>
        <Field label="Padding (px)"><Input type="number" min={0} max={64} value={values.padding ?? ""} onChange={(e) => onChange({ padding: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 text-[13px]" placeholder="12" data-testid="vb-set-padding" /></Field>
      </div>

      {/* Margin */}
      {s.margin && (
        <Field label="Margin (px)"><Input type="number" min={0} max={64} value={values.margin ?? ""} onChange={(e) => onChange({ margin: e.target.value === "" ? null : Number(e.target.value) })} className="h-9 text-[13px]" placeholder="0" data-testid="vb-set-margin" /></Field>
      )}

      {/* Width */}
      {s.width && (
        <Field label="Width">
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5" data-testid="vb-set-width">
            {[["full", "Full"], ["half", "1/2"], ["third", "1/3"], ["auto", "Auto"]].map(([k, lbl]) => {
              const active = (values.width || "full") === k;
              return (
                <button key={k} type="button" onClick={() => onChange({ width: k })} className={`h-7 px-2.5 rounded-md text-[11.5px] font-semibold ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`vb-set-width-${k}`}>
                  {lbl}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {/* Alignment */}
      {s.align && (
        <Field label="Alignment">
          <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5" data-testid="vb-set-align">
            {[["left", AlignLeft], ["center", AlignCenter], ["right", AlignRight]].map(([k, I]) => {
              const active = (values.align || "left") === k;
              return (
                <button key={k} type="button" onClick={() => onChange({ align: k })} className={`h-7 w-8 grid place-items-center rounded-md ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`vb-set-align-${k}`}>
                  <I className="w-3.5 h-3.5" />
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {/* Shadow */}
      <Field label="Shadow">
        <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5" data-testid="vb-set-shadow">
          {["none", "soft", "medium", "strong"].map((opt) => {
            const active = (values.shadow || "soft") === opt;
            return (
              <button key={opt} type="button" onClick={() => onChange({ shadow: opt })} className={`h-7 px-2.5 rounded-md text-[11.5px] font-semibold capitalize ${active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`vb-set-shadow-${opt}`}>
                {opt}
              </button>
            );
          })}
        </div>
      </Field>

      {isPlaced && (
        <button type="button" onClick={onRemove} className="w-full h-9 rounded-lg border border-red-200 text-red-600 text-[12.5px] font-semibold hover:bg-red-50 inline-flex items-center justify-center gap-1.5" data-testid="vb-settings-remove">
          <Trash2 className="w-3.5 h-3.5" /> Remove from canvas
        </button>
      )}

      <div className="pt-2 text-[11px] text-slate-400 border-t border-slate-100">
        Business logic (calculations, tax, discounts, stock) is never editable here.
      </div>
    </div>
  );
}

/* ─── Small field helpers ──────────────────────────────────────────── */

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{label}</Label>
      {children}
      {hint && <div className="text-[10.5px] text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function ColorInput({ value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={value || "#ffffff"} onChange={(e) => onChange(e.target.value)} className="h-9 w-9 rounded-lg border border-slate-200 cursor-pointer" data-testid={`${testId}-picker`} />
      <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Auto" className="h-9 text-[12px] font-mono" data-testid={testId} />
    </div>
  );
}
